// Weather provider: hourly historical conditions for an arbitrary date range.
//
// Primary source:  Open-Meteo historical archive API (ERA5 reanalysis, no key).
//                  Wind speed/direction/gusts, relative humidity, temperature,
//                  precipitation, hourly, any location, 1940 -> ~5 days ago.
// Fallback:        bundled METAR observations (offline demo data).
//
// Convention: `direction` is meteorological — the direction the wind blows FROM,
// in degrees (0 = from north). Use `towardDeg()` for the blowing-toward heading.

import metarData from "../data/metar_data.json";

const lerp = (a, b, t) => a + (b - a) * t;
const lerpAngle = (a, b, t) => {
  const delta = ((b - a + 540) % 360) - 180;
  return (a + delta * t + 360) % 360;
};

export const towardDeg = (fromDeg) => (fromDeg + 180) % 360;

// Timeline of hourly samples with interpolated lookup.
export class WeatherTimeline {
  // samples: [{ timeMs, speed (mph), direction (deg FROM), gusts (mph),
  //             humidity (%), tempC, precip (mm) }] sorted by timeMs
  constructor(samples, source) {
    this.samples = samples;
    this.source = source;
    // Rolling 48h precipitation total per sample (fuel-moisture "rain memory")
    let acc = 0;
    let j = 0;
    this.rain48 = samples.map((s, i) => {
      acc += s.precip || 0;
      while (samples[j].timeMs < s.timeMs - 48 * 3600 * 1000) {
        acc -= samples[j].precip || 0;
        j++;
      }
      return acc;
    });
  }

  get startMs() {
    return this.samples[0]?.timeMs ?? 0;
  }
  get endMs() {
    return this.samples[this.samples.length - 1]?.timeMs ?? 0;
  }

  // Interpolated conditions at an arbitrary time
  at(timeMs) {
    const s = this.samples;
    if (s.length === 0) {
      return {
        speed: 10,
        direction: 0,
        gusts: 15,
        humidity: 40,
        tempC: 20,
        precip: 0,
        rain48: 0,
      };
    }
    if (timeMs <= s[0].timeMs) return { ...s[0], rain48: this.rain48[0] };
    if (timeMs >= s[s.length - 1].timeMs)
      return { ...s[s.length - 1], rain48: this.rain48[s.length - 1] };
    // Binary search for the bracketing pair
    let lo = 0,
      hi = s.length - 1;
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if (s[mid].timeMs <= timeMs) lo = mid;
      else hi = mid;
    }
    const t = (timeMs - s[lo].timeMs) / (s[hi].timeMs - s[lo].timeMs);
    return {
      speed: lerp(s[lo].speed, s[hi].speed, t),
      direction: lerpAngle(s[lo].direction, s[hi].direction, t),
      gusts: lerp(s[lo].gusts ?? s[lo].speed, s[hi].gusts ?? s[hi].speed, t),
      humidity: lerp(s[lo].humidity, s[hi].humidity, t),
      tempC: lerp(s[lo].tempC, s[hi].tempC, t),
      precip: lerp(s[lo].precip, s[hi].precip, t),
      rain48: lerp(this.rain48[lo], this.rain48[hi], t),
    };
  }
}

// --- Open-Meteo archive --------------------------------------------------
const toDateStr = (d) => d.toISOString().slice(0, 10);

export const fetchWeatherTimeline = async (
  { lat, lon, startDate, endDate },
  onProgress
) => {
  onProgress?.("Fetching historical weather (Open-Meteo)…");
  const params = new URLSearchParams({
    latitude: lat.toFixed(4),
    longitude: lon.toFixed(4),
    start_date: toDateStr(startDate),
    end_date: toDateStr(endDate),
    hourly:
      "temperature_2m,relative_humidity_2m,precipitation,wind_speed_10m,wind_direction_10m,wind_gusts_10m",
    wind_speed_unit: "mph",
    timezone: "UTC",
  });
  const resp = await fetch(
    `https://archive-api.open-meteo.com/v1/archive?${params}`
  );
  if (!resp.ok) {
    const body = await resp.text().catch(() => "");
    throw new Error(`Open-Meteo archive HTTP ${resp.status}: ${body}`);
  }
  const json = await resp.json();
  const h = json.hourly;
  if (!h?.time?.length) throw new Error("Open-Meteo returned no hourly data");

  const samples = [];
  for (let i = 0; i < h.time.length; i++) {
    // Trailing hours of a recent range can be null (ERA5 lags ~5 days)
    if (h.wind_speed_10m[i] === null || h.relative_humidity_2m[i] === null)
      continue;
    samples.push({
      timeMs: Date.parse(`${h.time[i]}:00Z`),
      speed: h.wind_speed_10m[i],
      direction: h.wind_direction_10m[i] ?? 0,
      gusts: h.wind_gusts_10m[i] ?? h.wind_speed_10m[i],
      humidity: h.relative_humidity_2m[i],
      tempC: h.temperature_2m[i] ?? 15,
      precip: h.precipitation[i] ?? 0,
    });
  }
  if (samples.length === 0) {
    throw new Error(
      "No weather data for this date range (the archive lags ~5 days behind today)."
    );
  }
  return new WeatherTimeline(samples, "Open-Meteo ERA5 archive");
};

// --- METAR fallback (offline demo) ---------------------------------------
const KNOTS_TO_MPH = 1.15078;

const calculateHumidity = (tempC, dewPointC) => {
  if (tempC === null || dewPointC === null) return 50;
  if (tempC <= dewPointC) return 100;
  const b = 17.625,
    c = 243.04;
  const es = Math.exp((b * tempC) / (c + tempC));
  const e = Math.exp((b * dewPointC) / (c + dewPointC));
  return Math.max(0, Math.min(100, (e / es) * 100));
};

const parseMetar = (metarString) => {
  if (!metarString) return null;
  const windMatch = metarString.match(/(\d{3}|VRB)(\d{2,3})(?:G(\d{2,3}))?KT/);
  const tempDewMatch = metarString.match(/(?:^|\s)(M?\d{2})\/(M?\d{2})(?:$|\s)/);
  if (!windMatch || !tempDewMatch) return null;
  const parseTemp = (s) =>
    s.startsWith("M") ? -parseInt(s.slice(1), 10) : parseInt(s, 10);
  const tempC = parseTemp(tempDewMatch[1]);
  const dewPointC = parseTemp(tempDewMatch[2]);
  return {
    direction: windMatch[1] === "VRB" ? 0 : parseInt(windMatch[1], 10),
    speed: parseInt(windMatch[2], 10) * KNOTS_TO_MPH,
    gusts: windMatch[3]
      ? parseInt(windMatch[3], 10) * KNOTS_TO_MPH
      : parseInt(windMatch[2], 10) * KNOTS_TO_MPH,
    humidity: calculateHumidity(tempC, dewPointC),
    tempC,
    precip: 0,
  };
};

export const metarFallbackTimeline = () => {
  const samples = metarData
    .map((rep) => {
      const parsed = parseMetar(rep.metar_string);
      if (!parsed) return null;
      return { timeMs: Date.parse(rep.timestamp_iso), ...parsed };
    })
    .filter(Boolean)
    .sort((a, b) => a.timeMs - b.timeMs);
  return new WeatherTimeline(samples, "Bundled METAR (offline fallback)");
};
